import type { ReactNode } from 'react'
import React, { useCallback, useEffect, useRef } from 'react'
import { tv } from 'tailwind-variants'

import type { Option, OptionValue } from '@ui-kit/components/formComponents/Select/types'
import { getOptionIdentifier } from '@ui-kit/components/formComponents/Select/utils'

const menuContainer = tv({
  base: 'relative',
  variants: {
    focused: {
      true: 'z-[9998]',
    },
  },
})

interface Props {
  value?: OptionValue
  name: string
  onFocus?: () => void
  onBlur?: () => void
  disabled?: boolean
  options: Option[]
  isFocused?: boolean
  setIsFocused: (value: boolean) => void
  onSelectOptionHandler: (value: OptionValue) => void
  activeMenuOption?: string
  setActiveMenuOption: (value: string) => void
  containerRef?: React.RefObject<HTMLDivElement | null>
  children: ReactNode
}

const MenuContainerComponent = (props: Props) => {
  const {
    onBlur,
    onFocus,
    disabled,
    activeMenuOption,
    setActiveMenuOption,
    onSelectOptionHandler,
    options,
    isFocused,
    setIsFocused,
  } = props

  const internalRef = useRef<HTMLDivElement>(null)
  const selectContainerRef = props.containerRef ?? internalRef

  const blurField = useCallback(() => {
    setIsFocused(false)

    onBlur?.()
  }, [onBlur, setIsFocused])

  const focusField = useCallback(() => {
    if (disabled) {
      return
    }

    setIsFocused(true)

    onFocus?.()
  }, [onFocus, disabled, setIsFocused])

  const onToggleOptionsContainerHandler = () => {
    if (isFocused) {
      setActiveMenuOption('')

      return blurField()
    }

    setActiveMenuOption(getOptionIdentifier(props.value || '', props.name))

    focusField()
  }

  const onOptionKeydownHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (['ArrowUp', 'ArrowDown', 'Tab', 'Enter'].includes(event.nativeEvent.code)) {
      const highlightedValue = activeMenuOption?.split('-select-option-')[1] || props.value || ''

      const highlightedIndex = options.findIndex(
        (option) => option.value.toString() === highlightedValue
      )

      const selectedValue = options[highlightedIndex]

      switch (event.nativeEvent.code) {
        case 'ArrowUp':
          if (highlightedIndex - 1 < 0) {
            break
          }

          setActiveMenuOption(getOptionIdentifier(options[highlightedIndex - 1].value, props.name))
          break
        case 'ArrowDown':
        case 'Tab':
          if (highlightedIndex + 1 >= options.length) {
            break
          }

          setActiveMenuOption(getOptionIdentifier(options[highlightedIndex + 1].value, props.name))
          break
        case 'Enter':
          if (selectedValue) {
            onSelectOptionHandler(selectedValue.value)
            onToggleOptionsContainerHandler()
          }
          break
        default:
          break
      }
    }
  }

  useEffect(() => {
    const handleClickOutsideField = (e: MouseEvent) => {
      if (!selectContainerRef.current) {
        return
      }

      // Cast to HTMLElement so we can call closest() — OptionsContainer renders
      // the dropdown via createPortal under document.body, so the dropdown is
      // NOT inside selectContainerRef and we have to opt it out of the
      // click-outside check via its data attribute.
      const target = e.target as HTMLElement
      if (selectContainerRef.current.contains(target)) {
        return
      }
      if (target.closest?.(`[data-select-dropdown="${props.name}"]`)) {
        return
      }

      blurField()
    }

    document.addEventListener('pointerdown', handleClickOutsideField)

    return () => document.removeEventListener('pointerdown', handleClickOutsideField)
  }, [selectContainerRef, blurField, props.name])

  return (
    <div
      ref={selectContainerRef}
      onKeyDown={onOptionKeydownHandler}
      onClick={onToggleOptionsContainerHandler}
      className={menuContainer({ focused: isFocused })}
      id={props.name}
      role="listbox"
      aria-labelledby={`${props.name}-label`}
      aria-activedescendant={activeMenuOption || undefined}
      tabIndex={0}
    >
      {props.children}
    </div>
  )
}

export default MenuContainerComponent
